import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { User, ProfilePrivacy } from '../types';
import type { ActivityChartVariant, ActivityDashboardChartVariants } from '../types/activityChartVariant';
import { Capacitor } from '@capacitor/core';
import { firestoreRestApi } from '../utils/firestoreRestApi';
import { auth, functions } from '../firebase/config';
import { getTokens, isTokenExpired, updateTokens } from '../utils/tokenStorage';
import { refreshIdToken } from '../utils/firebaseAuthRestApi';

export const userService = {
  // Get all athletes (users with role 'athlete')
  async getAthletes(): Promise<User[]> {
    const q = query(
      collection(db, 'users'),
      where('role', '==', 'athlete'),
      orderBy('displayName', 'asc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt,
      updatedAt: doc.data().updatedAt,
    })) as User[];
  },

  // Get athletes not already in a specific team
  // NOTE: This method should only be used for manual team member addition by coaches
  // Athletes should primarily join teams via invitation codes
  async getAvailableAthletes(teamId: string): Promise<User[]> {
    // First get all athletes
    const allAthletes = await this.getAthletes();
    
    // Then get team members for this team
    const teamMembersQuery = query(
      collection(db, 'teamMembers'),
      where('teamId', '==', teamId)
    );
    const teamMembersSnapshot = await getDocs(teamMembersQuery);
    const teamMemberIds = teamMembersSnapshot.docs.map(doc => doc.data().athleteId);
    
    // Filter out athletes already in the team
    return allAthletes.filter(athlete => !teamMemberIds.includes(athlete.id));
  },

  // Get user by ID
  async getUserById(userId: string): Promise<User | null> {
    try {
      console.log('[UserService] getUserById called for:', userId);
      
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        console.log('[UserService] iOS detected - using REST API for getUserById');
        const userDoc = await firestoreRestApi.getDocument('users', userId);
        console.log('[UserService] REST API returned userDoc:', userDoc ? 'found' : 'null');
        
        if (!userDoc) {
          console.warn('[UserService] User not found for ID:', userId);
          return null;
        }
        
        // Check if userDoc has data() method or is already the data
        const docData = typeof userDoc.data === 'function' ? userDoc.data() : userDoc;
        console.log('[UserService] User data:', { id: userId, displayName: docData.displayName, email: docData.email });
        
        return {
          id: userId,
          ...docData,
          createdAt: docData.createdAt || Timestamp.now(),
          updatedAt: docData.updatedAt || Timestamp.now(),
        } as User;
      }
      
      // On web/Android, use SDK
      const userDoc = await getDoc(doc(db, 'users', userId));
      
      if (!userDoc.exists()) {
        console.warn('[UserService] User not found for ID:', userId);
        return null;
      }
      
      console.log('[UserService] User data from SDK:', { id: userId, displayName: userDoc.data().displayName });
      return {
        id: userDoc.id,
        ...userDoc.data(),
        createdAt: userDoc.data().createdAt,
        updatedAt: userDoc.data().updatedAt,
      } as User;
    } catch (error: any) {
      console.error('[UserService] getUserById: Error for userId', userId, ':', error);
      // Return null on timeout/error to prevent app from hanging
      return null;
    }
  },

  // Get default privacy settings
  getDefaultPrivacySettings(): ProfilePrivacy {
    return {
      visibility: 'team-only',
      showAvatar: true,
      showBadges: true,
      showPRs: true,
      showStats: true,
      showTeam: true,
    };
  },

  async updateActivityChartVariant(userId: string, variant: ActivityChartVariant): Promise<void> {
    await this.updateUser(userId, { activityChartVariant: variant });
  },

  async updateActivityChartVariants(userId: string, variants: ActivityDashboardChartVariants): Promise<void> {
    await this.updateUser(userId, { activityChartVariants: variants } as Partial<User>);
  },

  async updateWeekStartsOn(userId: string, weekStartsOn: 0 | 1 | 2 | 3 | 4 | 5 | 6): Promise<void> {
    await this.updateUser(userId, { weekStartsOn } as Partial<User>);
  },

  // Update user's profile privacy settings
  async updateProfilePrivacy(userId: string, privacy: ProfilePrivacy): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        profilePrivacy: privacy,
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      // If SDK fails with permission error, try REST API fallback
      if (error?.code === 'permission-denied') {
        console.log('[UserService] SDK permission denied; retrying updateProfilePrivacy via REST API');
        try {
          await firestoreRestApi.setDocument('users', userId, {
            profilePrivacy: privacy,
            updatedAt: new Date().toISOString(),
          }, true); // merge=true to only update specified fields
          return;
        } catch (restError: any) {
          console.error('[UserService] REST API fallback also failed for updateProfilePrivacy:', restError);
          throw restError;
        }
      }
      throw error;
    }
  },

  // Update user fields (generic update method)
  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      // If SDK fails with permission error, try REST API fallback
      if (error?.code === 'permission-denied') {
        console.log('[UserService] SDK permission denied; retrying updateUser via REST API');
        try {
          // Use setDocument with merge=true to update only specific fields
          await firestoreRestApi.setDocument('users', userId, {
            ...updates,
            updatedAt: new Date().toISOString(),
          }, true); // merge=true to only update specified fields
          return;
        } catch (restError: any) {
          console.error('[UserService] REST API fallback also failed:', restError);
          throw restError;
        }
      }
      throw error;
    }
  },

  /**
   * Update a user's display name.
   * - If targetUserId matches currentUserId: updates Firestore directly (user has permission for own doc).
   * - If targetUserId differs (coach editing team member): calls Cloud Function for secure server-side validation.
   * When updating own name, also syncs Firebase Auth displayName via the AuthContext/updateProfile flow if needed.
   */
  async updateDisplayName(
    targetUserId: string,
    newDisplayName: string,
    options?: { teamId?: string; currentUserId?: string }
  ): Promise<void> {
    const currentUserId = options?.currentUserId ?? auth.currentUser?.uid;
    if (!currentUserId) {
      throw new Error('You must be signed in to update a display name.');
    }

    const trimmed = String(newDisplayName).trim();
    if (!trimmed) {
      throw new Error('Display name cannot be empty.');
    }

    if (targetUserId === currentUserId) {
      await this.updateUser(targetUserId, { displayName: trimmed });
      return;
    }

    // Get a valid id token - Cloud Function requires it for auth
    // Prefer auth.currentUser; fallback to tokenStorage (e.g. when auth hasn't synced on web)
    let idToken: string | null = null;
    if (auth.currentUser) {
      idToken = await auth.currentUser.getIdToken(true);
    }
    if (!idToken) {
      const tokens = await getTokens();
      if (tokens) {
        const expired = await isTokenExpired();
        if (expired && tokens.refreshToken) {
          const refreshed = await refreshIdToken(tokens.refreshToken);
          idToken = refreshed.id_token;
          await updateTokens(refreshed.id_token, refreshed.refresh_token);
        } else {
          idToken = tokens.idToken;
        }
      }
    }
    if (!idToken) {
      throw new Error('Authentication session expired. Please refresh the page and try again.');
    }

    const projectId = auth.app.options.projectId;
    const url = `https://us-central1-${projectId}.cloudfunctions.net/updateDisplayName`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        data: {
          targetUserId,
          newDisplayName: trimmed,
          teamId: options?.teamId,
        },
      }),
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const message = errData?.error?.message
        || (response.status === 401 ? 'Authentication expired. Please refresh the page and try again.' : 'Failed to update display name.');
      throw new Error(message);
    }
  },

  // Check if viewer can see a user's profile
  async canViewProfile(viewerId: string, profileUserId: string): Promise<{
    canView: boolean;
    reason?: string;
  }> {
    // Same user can always view their own profile
    if (viewerId === profileUserId) {
      return { canView: true };
    }

    // Get the profile user's data
    const profileUser = await this.getUserById(profileUserId);
    if (!profileUser) {
      return { canView: false, reason: 'User not found' };
    }

    // Get privacy settings (default to team-only if not set)
    const privacy = profileUser.profilePrivacy || this.getDefaultPrivacySettings();

    // If profile is private, no one can view except the user themselves
    if (privacy.visibility === 'private') {
      return { canView: false, reason: 'This profile is private' };
    }

    // If profile is public, anyone can view
    if (privacy.visibility === 'public') {
      return { canView: true };
    }

    // If profile is team-only, check if viewer is on same team
    if (privacy.visibility === 'team-only') {
      const viewer = await this.getUserById(viewerId);
      if (!viewer) {
        return { canView: false, reason: 'Viewer not found' };
      }

      // Check if they share a team
      const viewerTeams = await this.getUserTeams(viewerId);
      const profileUserTeams = await this.getUserTeams(profileUserId);

      const sharedTeam = viewerTeams.some(vTeam => 
        profileUserTeams.some(pTeam => pTeam.id === vTeam.id)
      );

      if (sharedTeam) {
        return { canView: true };
      }

      return { canView: false, reason: 'This profile is only visible to teammates' };
    }

    return { canView: false, reason: 'Unknown privacy setting' };
  },

  // Get user's teams
  async getUserTeams(userId: string): Promise<Array<{id: string, name: string}>> {
    const teamIds: string[] = [];

    // On iOS, use REST API to avoid SDK query stalls in WebView/native bridge scenarios.
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const memberDocs = await firestoreRestApi.queryDocuments('teamMembers', [
          { field: 'athleteId', op: '==', value: userId }
        ]);
        teamIds.push(
          ...memberDocs
            .map((doc) => doc.data()?.teamId)
            .filter((id): id is string => typeof id === 'string' && id.length > 0)
        );

        // Support both legacy coachId and current headCoachId schemas.
        const [coachTeams, headCoachTeams] = await Promise.all([
          firestoreRestApi.queryDocuments('teams', [{ field: 'coachId', op: '==', value: userId }]).catch(() => []),
          firestoreRestApi.queryDocuments('teams', [{ field: 'headCoachId', op: '==', value: userId }]).catch(() => []),
        ]);

        teamIds.push(...coachTeams.map((doc) => doc.id));
        teamIds.push(...headCoachTeams.map((doc) => doc.id));

        const uniqueTeamIds = Array.from(new Set(teamIds));
        if (uniqueTeamIds.length === 0) return [];

        const teams = await Promise.all(
          uniqueTeamIds.map(async (teamId) => {
            try {
              const teamDoc = await firestoreRestApi.getDocument('teams', teamId);
              if (!teamDoc) return null;
              const data = teamDoc.data();
              return {
                id: teamDoc.id,
                name: data?.name || 'Unnamed Team',
              };
            } catch {
              return null;
            }
          })
        );

        return teams.filter((team): team is {id: string, name: string} => team !== null);
      } catch (error) {
        console.error('Error fetching user teams via REST API:', error);
        return [];
      }
    }

    // On web/Android, use SDK
    const teamMembersQuery = query(
      collection(db, 'teamMembers'),
      where('athleteId', '==', userId)
    );
    const teamMembersSnapshot = await getDocs(teamMembersQuery);
    teamIds.push(...teamMembersSnapshot.docs.map(doc => doc.data().teamId));

    // Support both legacy coachId and current headCoachId schemas.
    const [coachTeamsSnapshot, headCoachTeamsSnapshot] = await Promise.all([
      getDocs(query(collection(db, 'teams'), where('coachId', '==', userId))).catch(() => ({ docs: [] } as any)),
      getDocs(query(collection(db, 'teams'), where('headCoachId', '==', userId))).catch(() => ({ docs: [] } as any)),
    ]);
    teamIds.push(...coachTeamsSnapshot.docs.map((doc: any) => doc.id));
    teamIds.push(...headCoachTeamsSnapshot.docs.map((doc: any) => doc.id));

    const uniqueTeamIds = Array.from(new Set(teamIds));
    if (uniqueTeamIds.length === 0) return [];

    const teams = await Promise.all(
      uniqueTeamIds.map(async (teamId) => {
        const teamDoc = await getDoc(doc(db, 'teams', teamId));
        if (teamDoc.exists()) {
          return {
            id: teamDoc.id,
            name: teamDoc.data().name,
          };
        }
        return null;
      })
    );

    return teams.filter((team): team is {id: string, name: string} => team !== null);
  },

  // Get public profile data (respects privacy settings)
  async getPublicProfile(userId: string, viewerId: string) {
    const canViewResult = await this.canViewProfile(viewerId, userId);
    
    if (!canViewResult.canView) {
      return {
        canView: false,
        reason: canViewResult.reason,
        user: null,
      };
    }

    const user = await this.getUserById(userId);
    if (!user) {
      return {
        canView: false,
        reason: 'User not found',
        user: null,
      };
    }

    const privacy = user.profilePrivacy || this.getDefaultPrivacySettings();

    return {
      canView: true,
      user,
      privacy,
    };
  },
};
